import type {
    ActionToolInputSchemaItem,
    CapabilityBindingGetResponse,
    McpToolVisibility,
    TenantTool,
} from './mcptypes';

export interface ActionMcpUpsertSnapshot {
    sourceId: string;
    name: string;
    description: string;
    actionType: string;
    handleFunction: string;
    apiBody: string | Record<string, unknown>;
    actionRoute: string;
    actionAPIMethod: string;
}

export interface AdvisorMcpUpsertSnapshot {
    advisorId: string;
    name: string;
    description: string;
}

export interface AgentMcpUpsertSnapshot {
    aiworkerId: string;
    name: string;
    description: string;
}

interface ActionMcpUpsertPayloadInput extends ActionMcpUpsertSnapshot {
    mcpToolMappingEnabled: boolean;
    mcpDescription?: string;
    mcpVisibility?: McpToolVisibility;
    inputSchema: ActionToolInputSchemaItem[];
}

interface AdvisorMcpUpsertPayloadInput extends AdvisorMcpUpsertSnapshot {
    mcpToolMappingEnabled: boolean;
    mcpDescription?: string;
    mcpVisibility?: McpToolVisibility;
}

interface AgentMcpUpsertPayloadInput extends AgentMcpUpsertSnapshot {
    tenantId: string;
    mcpToolMappingEnabled: boolean;
    mcpDescription?: string;
    mcpVisibility?: McpToolVisibility;
}

const MCP_ACTION_TOOL_TYPE = 'action';
const MCP_ADVISOR_TOOL_TYPE = 'advisor';
const MCP_AGENT_TOOL_TYPE = 'agent';

function collapseUnderscoreSeparators(value: string): string {
    const segments: string[] = [];
    let segment = '';

    for (const char of value) {
        if (char === '_') {
            if (segment) {
                segments.push(segment);
                segment = '';
            }
            continue;
        }
        segment += char;
    }

    if (segment) {
        segments.push(segment);
    }

    return segments.join('_');
}

/**
 * Resolve whether MCP tool mapping is enabled from bindings/get and tenant tools list.
 * Binding.advisor-specific enabled takes precedence; then tenant_enabled on the get response;
 * then tenant tool row when get returns 404 or omits binding fields.
 */
export function resolveMcpToolMappingEnabled(
    bindingResponse: CapabilityBindingGetResponse | null | undefined,
    tenantTool?: TenantTool | null,
): boolean {
    const binding = bindingResponse?.binding;
    if (typeof binding?.enabled === 'boolean') {
        return binding.enabled;
    }

    if (typeof bindingResponse?.tenant_enabled === 'boolean') {
        return bindingResponse.tenant_enabled;
    }

    const raw = bindingResponse as Record<string, unknown> | null | undefined;
    if (raw && typeof raw.enabled === 'boolean') {
        return raw.enabled;
    }

    if (typeof tenantTool?.tenant_enabled === 'boolean') {
        return tenantTool.tenant_enabled;
    }

    return false;
}

/** Coerce a display label into the snake_case capability name used by MCP APIs. */
export function normalizeMcpCapabilityName(raw: string): string {
    let s = (raw ?? '').trim();

    s = s.replace(/([a-z0-9])([A-Z])/g, '$1_$2');
    s = s.replace(/([A-Z])([A-Z][a-z])/g, '$1_$2');
    s = s.toLowerCase();
    s = s.replace(/[^a-z0-9]+/g, '_');
    s = collapseUnderscoreSeparators(s);

    if (s && !/^[a-z]/.test(s[0])) {
        s = `a_${s}`;
    }

    return s;
}

export function parseApiBodyObject(apiBody: string | Record<string, unknown> | unknown): Record<string, unknown> {
    if (apiBody === null || apiBody === undefined || apiBody === '') {
        return {};
    }

    if (typeof apiBody === 'object' && !Array.isArray(apiBody)) {
        return apiBody as Record<string, unknown>;
    }

    if (typeof apiBody !== 'string') {
        return {};
    }

    const trimmed = apiBody.trim();
    if (!trimmed || trimmed === '[object Object]') {
        return {};
    }

    try {
        let parsed: unknown = JSON.parse(trimmed);

        if (typeof parsed === 'string') {
            try {
                parsed = JSON.parse(parsed);
            } catch {
                return {};
            }
        }

        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? (parsed as Record<string, unknown>)
            : {};
    } catch {
        return {};
    }
}

export function parseInputSchemaJson(inputSchemaJson: string): ActionToolInputSchemaItem[] {
    const parsed = JSON.parse(inputSchemaJson);
    if (!Array.isArray(parsed)) {
        throw new Error('Input schema must be a JSON array');
    }

    return parsed.map((item, index) => {
        if (!item || typeof item !== 'object' || Array.isArray(item)) {
            throw new Error(`Input schema item at index ${index} must be an object`);
        }

        const name = typeof item.name === 'string' ? item.name.trim() : '';
        if (!name) {
            throw new Error(`Input schema item at index ${index} must include a non-empty "name"`);
        }

        return {
            name,
            type: typeof item.type === 'string' ? item.type : 'string',
            required: item.required !== false,
            ...(typeof item.description === 'string' ? { description: item.description } : {}),
            ...('default' in item ? { default: item.default } : {}),
        };
    });
}

export function buildMcpUpsertSnapshotFromAction(action: Record<string, any> | undefined): ActionMcpUpsertSnapshot | null {
    if (!action) {
        return null;
    }

    const sourceId = action.source_id ?? action.aiworker_id;
    const name = action.name?.trim();
    if (!sourceId || !name) {
        return null;
    }

    const attributes: Record<string, any> = {};
    action.attributes?.forEach((item: any) => {
        attributes[item?.attr_name] = item;
    });

    const apiRequest: Record<string, any> = attributes?.api_spec?.attr_json ?? {};
    const apiType = attributes?.api_type?.attr_value ?? '';

    if (apiType === 'internal_api') {
        return {
            sourceId,
            name,
            description: action.description ?? '',
            actionType: apiType,
            handleFunction: apiRequest?.name ?? '',
            apiBody: apiRequest?.sample_payload ?? {},
            actionRoute: '',
            actionAPIMethod: '',
        };
    }

    return {
        sourceId,
        name,
        description: action.description ?? '',
        actionType: apiType,
        handleFunction: '',
        apiBody: apiRequest?.payload ?? {},
        actionRoute: apiRequest?.route ?? '',
        actionAPIMethod: apiRequest?.method ?? '',
    };
}

export function resolveMcpUpsertSnapshot(
    liveSnapshot: ActionMcpUpsertSnapshot | null | undefined,
    action: Record<string, any> | undefined,
): ActionMcpUpsertSnapshot | null {
    const fromAction = buildMcpUpsertSnapshotFromAction(action);

    if (!liveSnapshot && !fromAction) {
        return null;
    }

    if (!liveSnapshot) {
        return fromAction;
    }

    if (!fromAction) {
        return liveSnapshot;
    }

    const livePayload = parseApiBodyObject(liveSnapshot.apiBody);
    const hasLivePayload = Object.keys(livePayload).length > 0;

    return {
        ...fromAction,
        ...liveSnapshot,
        apiBody: hasLivePayload ? liveSnapshot.apiBody : fromAction.apiBody,
    };
}

export function buildActionMcpUpsertPayload({
    sourceId,
    name,
    description,
    apiBody,
    mcpToolMappingEnabled,
    mcpDescription,
    mcpVisibility,
    inputSchema,
}: ActionMcpUpsertPayloadInput) {
    const parsedBody = parseApiBodyObject(apiBody);

    return {
        tool_type: MCP_ACTION_TOOL_TYPE,
        name: normalizeMcpCapabilityName(name),
        description: mcpDescription?.trim() || description,
        ...(mcpVisibility ? { visibility: mcpVisibility } : {}),
        payload_template: {
            source_id: sourceId,
            payload: parsedBody,
        },
        inputSchema,
        requires_binding: true,
        enabled: mcpToolMappingEnabled,
    };
}

/** @deprecated Use buildActionMcpUpsertPayload */
export const buildMcpUpsertPayload = buildActionMcpUpsertPayload;

export function buildAdvisorMcpUpsertPayload({
    advisorId,
    name,
    description,
    mcpToolMappingEnabled,
    mcpDescription,
    mcpVisibility,
}: AdvisorMcpUpsertPayloadInput) {
    return {
        tool_type: MCP_ADVISOR_TOOL_TYPE,
        name: normalizeMcpCapabilityName(name),
        description: mcpDescription?.trim() || description,
        ...(mcpVisibility ? { visibility: mcpVisibility } : {}),
        advisor_id: advisorId,
        requires_binding: true,
        enabled: mcpToolMappingEnabled,
    };
}

export function buildAgentMcpUpsertPayload({
    tenantId,
    aiworkerId,
    name,
    description,
    mcpToolMappingEnabled,
    mcpDescription,
    mcpVisibility,
}: AgentMcpUpsertPayloadInput) {
    return {
        tenant_id: tenantId,
        tool_type: MCP_AGENT_TOOL_TYPE,
        name: normalizeMcpCapabilityName(name),
        description: mcpDescription?.trim() || description,
        aiworker_id: aiworkerId,
        enabled: mcpToolMappingEnabled,
        ...(mcpVisibility ? { visibility: mcpVisibility } : {}),
    };
}
